import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bot, Download, Eye, FileText, LoaderCircle, MessageCircleMore, Mic, Plus, SendHorizonal, Sparkles, Square, Trash2, WandSparkles, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../utils/api';
import {
  CHAT_OPERACIONAL_COMPETENCIA_OPTIONS,
  CHAT_OPERACIONAL_MODULE_OPTIONS,
  CHAT_OPERACIONAL_STATUS_OPTIONS,
  CHAT_OPERACIONAL_STATUS_PROMPTS,
  MANNU_GUIDED_ACTION_CATEGORIES,
} from './mannuHubActionCatalog';

const ADMIN_SUGGESTIONS = [
  'Qual o prazo da DEFIS?',
  'Quantas tarefas concluí este mês?',
  'Quais clientes estão com pendência?',
  'Qual cliente tem mais débitos?',
];

const COLABORADOR_SUGGESTIONS = [
  'Quais tarefas eu tenho hoje?',
  'Quais tarefas estão atrasadas?',
  'Qual cliente estou responsável?',
  'O que preciso fazer agora?',
];

const LOOKUP_REQUEST_OPTIONS = {
  das: [
    { value: 'recibo', label: 'Buscar recibo' },
    { value: 'extrato', label: 'Buscar extrato' },
    { value: 'guia', label: 'Buscar guia' },
  ],
  dctfweb: [
    { value: 'all', label: 'Buscar tudo' },
    { value: 'recibo', label: 'Buscar recibo' },
  ],
  pgdas: [
    { value: 'declaracao', label: 'Buscar declaração' },
    { value: 'extrato', label: 'Buscar extrato da declaração' },
    { value: 'recibo', label: 'Buscar recibo' },
  ],
  defis: [
    { value: 'all', label: 'Buscar tudo' },
    { value: 'recibo', label: 'Buscar recibo' },
  ],
  mit: [
    { value: 'all', label: 'Buscar tudo' },
    { value: 'recibo', label: 'Buscar recibo' },
  ],
};

const LOOKUP_TITLES = {
  das: 'Escolha o mês do DAS',
  dctfweb: 'Escolha a competência da DCTFWeb',
  pgdas: 'Escolha a competência do PGDAS-D',
  defis: 'Escolha o ano-calendário da DEFIS',
  mit: 'Escolha a competência do MIT',
};

const LOOKUP_DESCRIPTIONS = {
  das: 'Escolha o período e depois selecione exatamente qual documento do DAS você quer buscar.',
  dctfweb: 'Vou buscar o recibo e os documentos disponíveis da DCTFWeb para a competência escolhida.',
  pgdas: 'Escolha a competência e depois selecione exatamente qual PDF do PGDAS-D você precisa: declaração, extrato da declaração ou recibo.',
  defis: 'Vou buscar o recibo e os documentos disponíveis da DEFIS para o ano-calendário escolhido.',
  mit: 'Vou buscar o recibo e os documentos disponíveis do MIT para a competência escolhida.',
};

const buildLookupFormState = (action) => {
  const obligationCode = action.obligation_code || 'das';
  const pickerMode = action.picker_mode || (obligationCode === 'defis' ? 'year' : 'month');
  const nowMonth = new Date().toISOString().slice(0, 7);
  const currentYear = new Date().getFullYear();
  const rawPeriod = String(action.periodo_sugerido || '');
  const period = pickerMode === 'year'
    ? (rawPeriod.slice(0, 4) || String(currentYear))
    : (rawPeriod.match(/^\d{6}$/) ? rawPeriod.replace(/(\d{4})(\d{2})/, '$1-$2') : (rawPeriod || nowMonth));

  return {
    client_id: action.client_id,
    cnpj: action.cnpj,
    period,
    picker_mode: pickerMode,
    obligation_code: obligationCode,
    obligation_label: action.obligation_label || obligationCode.toUpperCase(),
    title: LOOKUP_TITLES[obligationCode] || `Escolha o período de ${obligationCode.toUpperCase()}`,
    description: LOOKUP_DESCRIPTIONS[obligationCode] || 'Vou buscar os documentos disponíveis para o período escolhido.',
    request_options: action.request_options?.length ? action.request_options : (LOOKUP_REQUEST_OPTIONS[obligationCode] || LOOKUP_REQUEST_OPTIONS.das),
  };
};

const normalizeLookupPeriodForRequest = (lookupForm) => {
  if (!lookupForm) return '';
  if (lookupForm.picker_mode === 'year') {
    return String(lookupForm.period || '').replace(/\D/g, '').slice(0, 4);
  }
  return String(lookupForm.period || '').replace('-', '').slice(0, 6);
};

const buildDasOverdueFormState = (action) => ({
  client_id: action.client_id,
  cnpj: action.cnpj,
  generation_date: '',
  selected_periods: [],
  period_options: action.period_options || [],
});

const buildMeiInstallmentFormState = (action) => ({
  client_id: action.client_id,
  cnpj: action.cnpj,
  parcelamento_tipo: action.parcelamento_tipo,
  numero_pedido: action.numero_pedido,
  numero_parcela: '',
  parcel_options: action.parcel_options || [],
});

export const MannuAIWidget = ({
  token,
  initialOpen = false,
  standalone = false,
  onClose = null,
  guidedCatalog = false,
  defaultCompanyMode = false,
  titleOverride = null,
  subtitleOverride = null,
}) => {
  const [open, setOpen] = useState(initialOpen);
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [userRole, setUserRole] = useState('admin');
  const [pendingPrompt, setPendingPrompt] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [clientOptions, setClientOptions] = useState([]);
  const [activeClient, setActiveClient] = useState(null);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [dasForm, setDasForm] = useState(null);
  const [obligationLookupForm, setObligationLookupForm] = useState(null);
  const [dasOverdueForm, setDasOverdueForm] = useState(null);
  const [meiInstallmentForm, setMeiInstallmentForm] = useState(null);
  const [monitorStatus, setMonitorStatus] = useState({ active: false, lastRunAt: null, notificationsCreated: 0 });
  const [commandMode, setCommandMode] = useState(false);
  const [guidedModuleFilter, setGuidedModuleFilter] = useState('all');
  const [guidedStatusFilter, setGuidedStatusFilter] = useState('all');
  const [guidedCompetenciaFilter, setGuidedCompetenciaFilter] = useState('all');
  const [guidedManualCompetencia, setGuidedManualCompetencia] = useState('');
  const [guidedActionSearch, setGuidedActionSearch] = useState('');
  const endRef = useRef(null);
  const suppressSessionReloadRef = useRef(false);
  const mediaRecorderRef = useRef(null);
  const recordingStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const guidedCompanyPrimedRef = useRef(false);
  const guidedCatalogInitializedRef = useRef(false);

  const widgetTitle = titleOverride || (standalone ? 'App IA Operacional' : 'Mannu AI');
  const widgetSubtitle = subtitleOverride || (standalone ? 'A mesma inteligência do WhatsApp, agora em formato de app' : 'Assistente contábil e operacional do LBCC Hub');

  const suggestions = useMemo(
    () => (userRole === 'colaborador' ? COLABORADOR_SUGGESTIONS : ADMIN_SUGGESTIONS),
    [userRole],
  );

  const currentCompetenciaLabel = useMemo(() => {
    if (guidedCompetenciaFilter === 'current') {
      return 'mês atual';
    }
    if (guidedCompetenciaFilter === 'previous') {
      return 'mês anterior';
    }
    if (guidedCompetenciaFilter === 'manual' && guidedManualCompetencia) {
      const [year, month] = guidedManualCompetencia.split('-');
      return month && year ? `${month}/${year}` : guidedManualCompetencia;
    }
    return '';
  }, [guidedCompetenciaFilter, guidedManualCompetencia]);

  const visibleGuidedCategories = useMemo(() => {
    const normalizedSearch = guidedActionSearch.trim().toLowerCase();

    return MANNU_GUIDED_ACTION_CATEGORIES
      .filter((category) => guidedModuleFilter === 'all' || category.id === guidedModuleFilter)
      .map((category) => ({
        ...category,
        actions: category.actions.filter((action) => {
          if (guidedCompetenciaFilter !== 'all' && !action.supportsCompetencia) {
            return false;
          }
          if (guidedStatusFilter !== 'all' && !action.supportsStatus) {
            return false;
          }
          if (!normalizedSearch) {
            return true;
          }
          const haystack = `${action.label} ${action.description} ${action.prompt}`.toLowerCase();
          return haystack.includes(normalizedSearch);
        }),
      }))
      .filter((category) => category.actions.length > 0);
  }, [guidedActionSearch, guidedCompetenciaFilter, guidedModuleFilter, guidedStatusFilter]);

  useEffect(() => {
    if (initialOpen) {
      setOpen(true);
    }
  }, [initialOpen]);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const scrollToBottom = useCallback(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const loadSessions = useCallback(async () => {
    const response = await axios.get(`${API_URL}/api/mannu/sessions`, { headers: authHeaders });
    setSessions(response.data || []);
    if (!guidedCatalog && response.data?.length) {
      setActiveSessionId((currentSessionId) => currentSessionId || response.data[0].id);
    }
  }, [authHeaders, guidedCatalog]);

  const loadMessages = useCallback(async (sessionId) => {
    if (!sessionId) {
      setMessages([]);
      return;
    }
    const response = await axios.get(`${API_URL}/api/mannu/sessions/${sessionId}/messages`, { headers: authHeaders });
    setMessages((response.data || []).map((message) => ({
      ...message,
      liveData: message.liveData || message.live_data,
      correctionProposal: message.correctionProposal || message.correction_proposal,
    })));
    const sessionMeta = sessions.find((session) => session.id === sessionId);
    if (!guidedCatalog && sessionMeta?.memory?.active_client) {
      setActiveClient(sessionMeta.memory.active_client);
    }
    setActiveSessionId(sessionId);
  }, [authHeaders, guidedCatalog, sessions]);

  const loadMe = useCallback(async () => {
    const response = await axios.get(`${API_URL}/api/mannu/me`, { headers: authHeaders });
    setUserRole(response.data?.role || 'admin');
  }, [authHeaders]);

  const searchClients = useCallback(async (query) => {
    const response = await axios.get(`${API_URL}/api/mannu/clients/search`, {
      headers: authHeaders,
      params: { query },
    });
    setClientOptions(response.data || []);
  }, [authHeaders]);

  const openCompanySelector = useCallback(async () => {
    setCommandMode(false);
    setPendingPrompt('__company_context__');
    setClientSearch('');
    try {
      await searchClients('');
    } catch {
      toast.error('Erro ao carregar empresas cadastradas');
    }
  }, [searchClients]);

  const resetGuidedCatalogState = useCallback(() => {
    setMessages([]);
    setActiveClient(null);
    setPreviewDocument(null);
    setDasForm(null);
    setObligationLookupForm(null);
    setDasOverdueForm(null);
    setMeiInstallmentForm(null);
    setGuidedModuleFilter('all');
    setGuidedStatusFilter('all');
    setGuidedCompetenciaFilter('all');
    setGuidedManualCompetencia('');
    setGuidedActionSearch('');
  }, []);

  const initializeGuidedCatalogSession = useCallback(async () => {
    const response = await axios.post(`${API_URL}/api/mannu/sessions`, {}, { headers: authHeaders });
    const nextSession = response.data;
    suppressSessionReloadRef.current = true;
    setSessions((prev) => [nextSession, ...prev.filter((session) => session.id !== nextSession.id)]);
    setActiveSessionId(nextSession.id);
    resetGuidedCatalogState();
    guidedCompanyPrimedRef.current = true;
    await openCompanySelector();
  }, [authHeaders, openCompanySelector, resetGuidedCatalogState]);

  const buildGuidedPrompt = (action) => {
    const segments = [action.prompt];

    if (action.supportsCompetencia && currentCompetenciaLabel) {
      segments.push(`Considere a competência ${currentCompetenciaLabel}.`);
    }

    if (action.supportsStatus && guidedStatusFilter !== 'all') {
      segments.push(CHAT_OPERACIONAL_STATUS_PROMPTS[guidedStatusFilter]);
    }

    segments.push('Use a empresa ativa desta conversa como contexto principal.');
    return segments.join(' ');
  };

  const runMonitorTick = useCallback(async () => {
    const response = await axios.post(`${API_URL}/api/mannu/monitor/tick`, null, {
      headers: authHeaders,
      params: activeClient?.id ? { active_client_id: activeClient.id } : {},
    });
    setMonitorStatus({
      active: Boolean(response.data?.active),
      lastRunAt: response.data?.timestamp || new Date().toISOString(),
      notificationsCreated: response.data?.notifications_created || 0,
    });
    if ((response.data?.notifications_created || 0) > 0) {
      toast.success(`Mannu AI registrou ${response.data.notifications_created} alerta(s) no sininho`);
    }
  }, [activeClient?.id, authHeaders]);

  useEffect(() => {
    if (!open || !token) return;
    setLoading(true);
    Promise.all([loadSessions(), loadMe()])
      .catch(() => toast.error('Erro ao carregar a Mannu AI'))
      .finally(() => setLoading(false));
  }, [loadMe, loadSessions, open, token]);

  useEffect(() => {
    if (!open || !token || !guidedCatalog || guidedCatalogInitializedRef.current) return;
    guidedCatalogInitializedRef.current = true;
    initializeGuidedCatalogSession().catch(() => {
      guidedCatalogInitializedRef.current = false;
      toast.error('Erro ao preparar o chat operacional');
    });
  }, [guidedCatalog, initializeGuidedCatalogSession, open, token]);

  useEffect(() => {
    if (!open || !activeSessionId) return;
    if (suppressSessionReloadRef.current) {
      suppressSessionReloadRef.current = false;
      return;
    }
    loadMessages(activeSessionId).catch(() => toast.error('Erro ao carregar conversa'));
  }, [activeSessionId, loadMessages, open]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, open]);

  useEffect(() => {
    if (!open || !pendingPrompt) return;
    scrollToBottom();
    if (clientOptions.length === 0) {
      searchClients('').catch(() => toast.error('Erro ao carregar clientes cadastrados'));
    }
  }, [clientOptions.length, open, pendingPrompt, scrollToBottom, searchClients]);

  useEffect(() => {
    if (!open || !token || !activeSessionId) return undefined;
    runMonitorTick().catch(() => toast.error('Erro ao executar monitoramento da Mannu AI'));
    const interval = setInterval(() => {
      runMonitorTick().catch(() => null);
    }, 45000);
    return () => clearInterval(interval);
  }, [activeSessionId, open, runMonitorTick, token]);

  useEffect(() => {
    if (!open || !defaultCompanyMode || guidedCompanyPrimedRef.current || guidedCatalog) return;
    if (activeClient || pendingPrompt || messages.length > 0) return;
    guidedCompanyPrimedRef.current = true;
    openCompanySelector();
  }, [activeClient, defaultCompanyMode, guidedCatalog, messages.length, open, openCompanySelector, pendingPrompt]);

  useEffect(() => {
    if (!token) return undefined;

    const handleNotificationOpen = async (event) => {
      const detail = event.detail || {};
      suppressSessionReloadRef.current = true;
      setOpen(true);
      setActiveSessionId(null);
      setMessages([]);
      if (detail.clientId || detail.clientName) {
        setActiveClient((prev) => ({
          id: detail.clientId || prev?.id,
          razao_social: detail.clientName || prev?.razao_social || 'Cliente',
          cnpj: prev?.cnpj || '',
        }));
      }

      if (detail.correctionProposalId) {
        try {
          const response = await axios.get(`${API_URL}/api/mannu/corrections/${detail.correctionProposalId}`, { headers: authHeaders });
          setMessages((prev) => {
            if (prev.some((message) => message.correctionProposal?.id === response.data?.id)) {
              return prev;
            }
            return [
              ...prev,
              {
                id: `mannu-notification-${response.data?.id || Date.now()}`,
                role: 'assistant',
                content: `A Mannu AI encontrou uma correção pendente para ${detail.clientName || response.data?.client_name || 'este cliente'}. Revise os detalhes abaixo e aprove/reprove para resolver.`,
                correctionProposal: response.data,
                created_at: new Date().toISOString(),
              },
            ];
          });
          return;
        } catch {
          toast.error('Não consegui abrir a correção desta notificação');
        }
      }

      if (detail.action === 'mannu_correcao') {
        try {
          const response = await axios.get(`${API_URL}/api/mannu/corrections`, { headers: authHeaders });
          const notificationText = `${detail.title || ''} ${detail.message || ''}`.toLowerCase();
          const matchedProposal = (response.data || []).find((proposal) => notificationText.includes(String(proposal.client_name || '').toLowerCase()));
          if (matchedProposal) {
            setMessages((prev) => {
              if (prev.some((message) => message.correctionProposal?.id === matchedProposal.id)) {
                return prev;
              }
              return [
                ...prev,
                {
                  id: `mannu-notification-${matchedProposal.id}`,
                  role: 'assistant',
                  content: `A Mannu AI encontrou uma correção pendente para ${matchedProposal.client_name}. Revise os detalhes abaixo e aprove/reprove para resolver.`,
                  correctionProposal: matchedProposal,
                  created_at: new Date().toISOString(),
                },
              ];
            });
            return;
          }
        } catch {
          toast.error('Não consegui localizar a correção vinculada à notificação');
        }
      }

      setMessages((prev) => [
        ...prev,
        {
          id: `mannu-notification-generic-${Date.now()}`,
          role: 'assistant',
          content: `Notificação Mannu AI: ${detail.title || 'Novo alerta'}\n${detail.message || 'Abra o contexto para resolver.'}`,
          created_at: new Date().toISOString(),
        },
      ]);
    };

    window.addEventListener('open-mannu-from-notification', handleNotificationOpen);
    return () => window.removeEventListener('open-mannu-from-notification', handleNotificationOpen);
  }, [authHeaders, token]);

  const handleNewSession = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/mannu/sessions`, {}, { headers: authHeaders });
      const nextSession = response.data;
      setSessions((prev) => [nextSession, ...prev.filter((session) => session.id !== nextSession.id)]);
      setActiveSessionId(nextSession.id);
      setMessages([]);
      setPendingPrompt(null);
      setClientOptions([]);
      setClientSearch('');
      setActiveClient(null);
      setPreviewDocument(null);
      setDasForm(null);
      setObligationLookupForm(null);
      setDasOverdueForm(null);
      setMeiInstallmentForm(null);
      guidedCompanyPrimedRef.current = false;
      if (defaultCompanyMode) {
        await openCompanySelector();
      }
    } catch {
      toast.error('Erro ao criar nova conversa da Mannu AI');
    }
  };

  const handleDeleteSession = async (sessionId) => {
    try {
      await axios.delete(`${API_URL}/api/mannu/sessions/${sessionId}`, { headers: authHeaders });
      const remaining = sessions.filter((session) => session.id !== sessionId);
      setSessions(remaining);
      if (activeSessionId === sessionId) {
        setActiveSessionId(remaining[0]?.id || null);
        setMessages([]);
        setPendingPrompt(null);
        setClientOptions([]);
        setClientSearch('');
        setActiveClient(null);
        setPreviewDocument(null);
        setDasForm(null);
        setObligationLookupForm(null);
        setDasOverdueForm(null);
        setMeiInstallmentForm(null);
        guidedCompanyPrimedRef.current = false;
      }
      toast.success('Conversa apagada com sucesso');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao apagar conversa');
    }
  };

  const handleSend = async (forcedText, selectedClientId = null) => {
    const nextText = (forcedText ?? input).trim();
    if (!nextText || sending) return;

    if (pendingPrompt && !selectedClientId && forcedText === undefined) {
      const results = await axios.get(`${API_URL}/api/mannu/clients/search`, {
        headers: authHeaders,
        params: { query: nextText },
      });
      const options = results.data || [];
      setClientSearch(nextText);
      setClientOptions(options);
      setInput('');
      if (options.length === 1) {
        await handleClientSelect(options[0].id);
      } else if (options.length === 0) {
        toast.error('Nenhum cliente encontrado. Tente outro nome ou CNPJ.');
      }
      return;
    }

    const optimisticUserMessage = {
      id: `temp-user-${Date.now()}`,
      role: 'user',
      content: nextText,
      created_at: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, optimisticUserMessage]);
    setInput('');
    setSending(true);

    try {
      const response = await axios.post(
        `${API_URL}/api/mannu/chat`,
        {
          session_id: activeSessionId,
          message: nextText,
          selected_client_id: selectedClientId ?? activeClient?.id ?? null,
          command_mode: commandMode,
        },
        { headers: authHeaders },
      );

      const serverMessages = response.data?.messages || [];
      const newSessionId = response.data?.session_id;
      suppressSessionReloadRef.current = true;
      setActiveSessionId(newSessionId);
      if (response.data?.session_memory?.active_client) {
        setActiveClient(response.data.session_memory.active_client);
      }
      const pickerAction = (response.data?.live_data?.available_actions || []).find((action) => (
        action.type === 'pick_obligation_period' || action.type === 'pick_das_period'
      ));
      if (pickerAction) {
        setObligationLookupForm(buildLookupFormState(pickerAction));
      }
      if (response.data?.requires_client_selection) {
        setPendingPrompt(response.data.pending_message || nextText);
        setClientSearch('');
        setClientOptions(response.data.client_options || []);
        setMessages((prev) => [
          ...prev.filter((message) => message.id !== optimisticUserMessage.id),
          optimisticUserMessage,
          {
            id: `selector-${Date.now()}`,
            role: 'assistant',
            content: response.data.selection_reason || 'Selecione o cliente para continuar.',
            created_at: new Date().toISOString(),
          },
        ]);
      } else {
        setPendingPrompt(null);
        setClientOptions([]);
        setClientSearch('');
        const normalizedMessages = serverMessages.map((message) => ({
          ...message,
          liveData: message.liveData || message.live_data,
          correctionProposal: message.correctionProposal || message.correction_proposal,
        }));
        if (normalizedMessages[1] && response.data?.correction_proposal) {
          normalizedMessages[1] = { ...normalizedMessages[1], correctionProposal: response.data.correction_proposal };
        }
        if (normalizedMessages[1] && response.data?.live_data) {
          normalizedMessages[1] = { ...normalizedMessages[1], liveData: response.data.live_data };
        }
        setMessages((prev) => [
          ...prev.filter((message) => message.id !== optimisticUserMessage.id),
          ...normalizedMessages,
        ]);
      }
      await loadSessions();
    } catch (error) {
      setMessages((prev) => prev.filter((message) => message.id !== optimisticUserMessage.id));
      toast.error(error.response?.data?.detail || 'Erro ao falar com a Mannu AI');
      setInput(nextText);
    } finally {
      setSending(false);
    }
  };

  const handleClientSelect = async (clientId) => {
    if (!pendingPrompt) return;
    const selectedClient = clientOptions.find((client) => client.id === clientId) || null;
    const isSwitchingCompany = Boolean(activeClient?.id && selectedClient?.id && activeClient.id !== selectedClient.id);

    if (selectedClient) {
      setActiveClient(selectedClient);
      guidedCompanyPrimedRef.current = true;
    }

    if (pendingPrompt === '__company_context__') {
      setPendingPrompt(null);
      setClientOptions([]);
      setClientSearch('');
      if (isSwitchingCompany) {
        suppressSessionReloadRef.current = true;
        setActiveSessionId(null);
        setPreviewDocument(null);
        setDasForm(null);
        setObligationLookupForm(null);
        setMessages([]);
      }
      setMessages(() => ([
        {
          id: `company-context-${Date.now()}`,
          role: 'assistant',
          content: selectedClient
            ? `Empresa ativa definida: **${selectedClient.razao_social}** (${selectedClient.cnpj}). Agora toda a conversa ficará no contexto dessa empresa até você trocar.`
            : 'Empresa ativa definida para esta conversa.',
          created_at: new Date().toISOString(),
        },
      ]));
      toast.success('Empresa selecionada com sucesso');
      return;
    }

    await handleSend(pendingPrompt, clientId);
  };

  const handleModeSelection = async (mode) => {
    if (mode === 'livre') {
      setCommandMode(false);
      setActiveClient(null);
      setPendingPrompt(null);
      setClientOptions([]);
      setClientSearch('');
      setGuidedModuleFilter('all');
      setGuidedStatusFilter('all');
      setGuidedCompetenciaFilter('all');
      setGuidedManualCompetencia('');
      setGuidedActionSearch('');
      guidedCompanyPrimedRef.current = false;
      toast.success('Modo conversa livre ativado');
      return;
    }

    if (mode === 'comando') {
      setCommandMode(true);
      setPendingPrompt(null);
      setClientOptions([]);
      setClientSearch('');
      toast.success('Modo comando JSON ativado');
      return;
    }

    setCommandMode(false);

    if (activeClient) {
      suppressSessionReloadRef.current = true;
      setActiveSessionId(null);
      setMessages([]);
      setPreviewDocument(null);
      setDasForm(null);
      setObligationLookupForm(null);
      setActiveClient(null);
    }

    setPendingPrompt('__company_context__');
    setClientSearch('');
    guidedCompanyPrimedRef.current = true;
    await openCompanySelector();
  };

  const handleCorrectionAction = async (proposalId, action, suggestionId = null) => {
    try {
      const endpoint = suggestionId
        ? `${API_URL}/api/mannu/corrections/${proposalId}/suggestions/${suggestionId}/${action}`
        : `${API_URL}/api/mannu/corrections/${proposalId}/${action}`;
      const response = await axios.post(endpoint, {}, { headers: authHeaders });
      toast.success(action === 'approve' ? 'Item de correção aprovado com sucesso' : 'Item de correção rejeitado');
      setMessages((prev) => prev.map((message) => {
        if (message.correctionProposal?.id !== proposalId) return message;
        return {
          ...message,
          correctionProposal: response.data?.proposal || message.correctionProposal,
        };
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar correção');
    }
  };

  const handlePreviewPdf = async (document) => {
    if (document?.pdf_base64) {
      setPreviewDocument({
        ...document,
        url: `data:application/pdf;base64,${document.pdf_base64}`,
      });
      return;
    }

    const documentId = document?.documento_id || document?.id;
    if (!documentId) return;

    try {
      const response = await axios.get(`${API_URL}/api/documentos/${documentId}/preview`, {
        headers: authHeaders,
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' }));
      setPreviewDocument({
        ...document,
        url: blobUrl,
      });
    } catch {
      toast.error('Não consegui abrir o preview deste documento');
    }
  };

  const handleDownloadPdf = async (document) => {
    if (document?.pdf_base64) {
      const link = window.document.createElement('a');
      link.href = `data:application/pdf;base64,${document.pdf_base64}`;
      link.download = document.file_name || 'documento.pdf';
      link.click();
      return;
    }

    const documentId = document?.documento_id || document?.id;
    if (!documentId) return;

    try {
      const response = await axios.get(`${API_URL}/api/documentos/${documentId}/download`, {
        headers: authHeaders,
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' }));
      const link = window.document.createElement('a');
      link.href = blobUrl;
      link.download = document.file_name || document.nome_arquivo || 'documento.pdf';
      link.click();
    } catch {
      toast.error('Não consegui baixar este documento');
    }
  };

  const cleanupRecordingResources = () => {
    if (recordingStreamRef.current) {
      recordingStreamRef.current.getTracks().forEach((track) => track.stop());
      recordingStreamRef.current = null;
    }
    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
  };

  const handleToggleAudioRecording = async () => {
    if (isTranscribing) return;

    if (isRecording) {
      mediaRecorderRef.current?.stop();
      setIsRecording(false);
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia || typeof window.MediaRecorder === 'undefined') {
      toast.error('Seu navegador não suporta gravação de áudio neste momento');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordingStreamRef.current = stream;

      const preferredMimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'].find((type) => (
        typeof window.MediaRecorder.isTypeSupported === 'function' ? window.MediaRecorder.isTypeSupported(type) : false
      ));
      const recorder = preferredMimeType
        ? new window.MediaRecorder(stream, { mimeType: preferredMimeType })
        : new window.MediaRecorder(stream);

      audioChunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data?.size) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        const mimeType = recorder.mimeType || preferredMimeType || 'audio/webm';
        const extension = mimeType.includes('mp4') ? 'm4a' : 'webm';
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        cleanupRecordingResources();

        if (!audioBlob.size) {
          toast.error('Não consegui capturar seu áudio. Tente novamente.');
          return;
        }

        const formData = new FormData();
        formData.append('audio', new File([audioBlob], `mannu-ai-audio.${extension}`, { type: mimeType }));
        formData.append('language', 'pt');

        setIsTranscribing(true);
        try {
          const response = await axios.post(`${API_URL}/api/mannu/audio/transcribe`, formData, {
            headers: {
              ...authHeaders,
            },
          });
          const transcript = (response.data?.text || '').trim();
          if (!transcript) {
            toast.error('Não consegui transformar seu áudio em texto.');
            return;
          }
          toast.success('Áudio transcrito com sucesso');
          await handleSend(transcript);
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Erro ao transcrever áudio');
        } finally {
          setIsTranscribing(false);
        }
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
      toast.success('Gravação iniciada. Clique novamente para finalizar.');
    } catch {
      cleanupRecordingResources();
      setIsRecording(false);
      toast.error('Não consegui acessar o microfone. Verifique a permissão e tente novamente.');
    }
  };

  useEffect(() => () => {
    cleanupRecordingResources();
  }, []);

  const handleRecalculateDas = async () => {
    if (!dasForm?.cnpj || !dasForm?.periodo || !dasForm?.valor) return;
    try {
      const response = await axios.post(`${API_URL}/api/serpro/das/gerar`, {
        cnpj: dasForm.cnpj,
        periodo_apuracao: dasForm.periodo,
        valor_receita_bruta: Number(dasForm.valor),
        cliente_id: dasForm.client_id,
      }, { headers: authHeaders });
      if (response.data?.pdf_base64) {
        handlePreviewPdf({
          pdf_base64: response.data.pdf_base64,
          file_name: `DAS_${dasForm.periodo}_${dasForm.cnpj}.pdf`,
          label: response.data?.pdf_salvo ? 'PDF recalculado do DAS · salvo no cliente' : 'PDF recalculado do DAS',
        });
        if (response.data?.pdf_salvo) {
          toast.success('DAS recalculado e salvo automaticamente na pasta do cliente');
        } else {
          toast.success('DAS recalculado com sucesso');
        }
      } else {
        toast.success('DAS recalculado. PDF não disponível na resposta.');
      }
      setDasForm(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao recalcular DAS');
    }
  };

  const handleFetchObligationDocuments = async (requested = 'all') => {
    if (!obligationLookupForm?.client_id || !obligationLookupForm?.period) return;

    try {
      const period = normalizeLookupPeriodForRequest(obligationLookupForm);
      const response = await axios.post(`${API_URL}/api/mannu/obligation-documents`, {
        client_id: obligationLookupForm.client_id,
        obligation_code: obligationLookupForm.obligation_code,
        period,
        requested,
      }, { headers: authHeaders });

      const docs = response.data?.documents || [];
      setMessages((prev) => ([
        ...prev,
        {
          id: `mannu-obligation-docs-${Date.now()}`,
          role: 'assistant',
          content: response.data?.summary_message || (docs.length > 0
            ? `Encontrei ${docs.length} documento(s) de ${obligationLookupForm.obligation_label} para ${response.data?.periodo_consultado || obligationLookupForm.period}.`
            : `Não encontrei documentos de ${obligationLookupForm.obligation_label} para ${response.data?.periodo_consultado || obligationLookupForm.period}.`),
          liveData: response.data,
          created_at: new Date().toISOString(),
        },
      ]));
      if ((response.data?.documents || []).some((document) => document?.linked_task_id)) {
        window.dispatchEvent(new CustomEvent('obrigacoes-updated', {
          detail: {
            client_id: obligationLookupForm.client_id,
            obligation_code: obligationLookupForm.obligation_code,
            period,
          },
        }));
      }
      setObligationLookupForm(null);
    } catch {
      toast.error('Erro ao buscar documentos da obrigação');
    }
  };

  const handleToggleDasOverduePeriod = (period) => {
    setDasOverdueForm((prev) => {
      if (!prev) return prev;
      const selectedPeriods = prev.selected_periods.includes(period)
        ? prev.selected_periods.filter((item) => item !== period)
        : [...prev.selected_periods, period];
      return { ...prev, selected_periods: selectedPeriods };
    });
  };

  const handleGenerateDasOverdueDocuments = async () => {
    if (!dasOverdueForm?.client_id || !dasOverdueForm?.selected_periods?.length || !dasOverdueForm?.generation_date) return;

    try {
      const response = await axios.post(`${API_URL}/api/mannu/das-overdue-documents`, {
        client_id: dasOverdueForm.client_id,
        periods: dasOverdueForm.selected_periods,
        generation_date: dasOverdueForm.generation_date,
        requested: 'guia',
      }, { headers: authHeaders });

      const docs = response.data?.documents || [];
      setMessages((prev) => ([
        ...prev,
        {
          id: `mannu-das-overdue-${Date.now()}`,
          role: 'assistant',
          content: response.data?.summary_message || (docs.length > 0
            ? `Gerei ${docs.length} PDF(s) de segunda via para as competências selecionadas.`
            : 'Não encontrei PDFs de segunda via para as competências selecionadas.'),
          liveData: response.data,
          created_at: new Date().toISOString(),
        },
      ]));

      if (docs.some((document) => document?.linked_task_id)) {
        window.dispatchEvent(new CustomEvent('obrigacoes-updated', {
          detail: {
            client_id: dasOverdueForm.client_id,
            obligation_code: 'das',
            periods: dasOverdueForm.selected_periods,
          },
        }));
      }

      setDasOverdueForm(null);
      toast.success('Segundas vias geradas com sucesso');
    } catch {
      toast.error('Erro ao gerar segundas vias do DAS');
    }
  };

  const handleGenerateMeiInstallmentDAS = async (payload) => {
    try {
      const response = await axios.post(`${API_URL}/api/mannu/mei-installment-das`, payload, { headers: authHeaders });
      setMessages((prev) => ([
        ...prev,
        {
          id: `mannu-mei-installment-${Date.now()}`,
          role: 'assistant',
          content: response.data?.summary_message || 'Parcela do MEI gerada com sucesso.',
          liveData: response.data,
          created_at: new Date().toISOString(),
        },
      ]));
      setMeiInstallmentForm(null);
      toast.success('Parcela do MEI gerada com sucesso');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar parcela do MEI');
    }
  };

  const renderDocumentActions = (document, scopeKey = 'default', documentIndex = 0) => (
    <div key={`${scopeKey}-${document.documento_id || document.id || document.file_name || document.filename || document.label || 'document'}-${documentIndex}`} className="flex flex-wrap gap-2">
      <button
        type="button"
        onClick={() => handlePreviewPdf(document)}
        className="rounded-full border border-emerald-200 bg-white px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
        data-testid={`mannu-ai-doc-preview-${scopeKey}-${normalizeSuggestionId(document.label)}`}
      >
        <Eye className="mr-1 inline h-3.5 w-3.5" /> {document.label}
      </button>
      <button
        type="button"
        onClick={() => handleDownloadPdf(document)}
        className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
        data-testid={`mannu-ai-doc-download-${scopeKey}-${normalizeSuggestionId(document.label)}`}
      >
        <Download className="mr-1 inline h-3.5 w-3.5" /> Baixar PDF
      </button>
      {document.saved_to_client_folder && (
        <span className="inline-flex items-center rounded-full border border-emerald-200 bg-white px-3 py-1.5 text-xs font-medium text-emerald-700" data-testid={`mannu-ai-doc-saved-${scopeKey}-${normalizeSuggestionId(document.label)}`}>
          Salvo na pasta do cliente
        </span>
      )}
      {document.linked_task_title && (
        <span className="inline-flex items-center rounded-full border border-amber-200 bg-white px-3 py-1.5 text-xs font-medium text-amber-700" data-testid={`mannu-ai-doc-linked-task-${scopeKey}-${normalizeSuggestionId(document.label)}`}>
          Tarefa preenchida: {document.linked_task_title}
        </span>
      )}
    </div>
  );

  return (
    <>
      {!standalone && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setOpen((prev) => !prev);
          }}
          className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100 transition-colors"
          data-testid="mannu-ai-toggle-button"
        >
          <Sparkles className="h-4 w-4" />
          <span className="hidden sm:inline">Mannu AI</span>
        </button>
      )}

      {(open || standalone) && (
        <div
          className={standalone ? 'fixed inset-0 z-[90] bg-[#f9fafb]' : 'fixed inset-0 z-[80] bg-slate-950/20 backdrop-blur-[1px]'}
          onClick={(event) => {
            if (!standalone && event.target === event.currentTarget) {
              setOpen(false);
            }
          }}
          data-testid={standalone ? 'mannu-ai-standalone-overlay' : 'mannu-ai-overlay'}
        >
          <div
            className={standalone ? 'absolute inset-0 overflow-hidden bg-[#f9fafb]' : 'absolute right-4 top-20 bottom-4 w-[min(96vw,780px)] overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl'}
            onClick={(event) => event.stopPropagation()}
            data-testid={standalone ? 'mannu-ai-standalone-panel' : 'mannu-ai-panel'}
          >
            <div className={`flex h-full ${standalone ? 'bg-[#f9fafb]' : ''}`}>
              <aside className={`hidden md:flex ${standalone ? 'w-64 border-r border-black/5 bg-white/80 backdrop-blur' : 'w-56 border-r border-slate-200 bg-slate-50'} flex-col`}>
                <div className="flex items-center justify-between px-4 py-4 border-b border-slate-200">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{standalone ? 'App IA' : 'Mannu AI'}</p>
                    <p className="text-xs text-slate-500">Conversas</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleNewSession}
                    className="rounded-full border border-slate-300 p-2 text-slate-600 hover:bg-white"
                    data-testid="mannu-ai-new-session-button"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {sessions.map((session) => (
                    <div
                      key={session.id}
                      className={`w-full rounded-2xl border px-3 py-3 transition-colors ${activeSessionId === session.id ? 'border-emerald-300 bg-white text-emerald-700' : 'border-transparent bg-slate-100 text-slate-600 hover:bg-white'}`}
                      data-testid={`mannu-ai-session-${session.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <button
                          type="button"
                          onClick={() => loadMessages(session.id)}
                          className="min-w-0 flex-1 text-left"
                          data-testid={`mannu-ai-session-open-${session.id}`}
                        >
                          <p className="text-sm font-medium line-clamp-2">{session.title || 'Nova conversa'}</p>
                          <p className="mt-1 text-xs text-slate-400 line-clamp-2">{session.last_message_preview || 'Sem mensagens ainda'}</p>
                        </button>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDeleteSession(session.id);
                          }}
                          className="rounded-full border border-slate-200 p-2 text-slate-400 hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                          data-testid={`mannu-ai-session-delete-${session.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {sessions.length === 0 && !loading && (
                    <p className="px-1 text-sm text-slate-500">Nenhuma conversa ainda. Clique em + para começar.</p>
                  )}
                </div>
              </aside>

              <section className={`flex min-w-0 flex-1 flex-col ${standalone ? 'bg-[#f9fafb]' : ''}`}>
                <div className={`flex items-center justify-between border-b px-5 py-4 ${standalone ? 'border-black/5 bg-white/80 backdrop-blur-xl' : 'border-slate-200'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${standalone ? 'bg-emerald-600 text-white' : 'bg-emerald-100 text-emerald-700'}`}>
                      <Bot className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-slate-900">{widgetTitle}</h3>
                      <p className="text-xs text-slate-500">{widgetSubtitle}</p>
                      <p className="mt-1 text-[11px] font-medium text-emerald-700" data-testid="mannu-ai-monitor-status">
                        {monitorStatus.active ? `Monitoramento ativo${monitorStatus.lastRunAt ? ` • última varredura ${new Date(monitorStatus.lastRunAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : ''}` : 'Monitoramento em espera'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleNewSession}
                      className="rounded-full border border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 md:hidden"
                      data-testid="mannu-ai-mobile-new-session-button"
                    >
                      Nova conversa
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (standalone && onClose) {
                          onClose();
                          return;
                        }
                        setOpen(false);
                      }}
                      className="rounded-full border border-slate-300 p-2 text-slate-600 hover:bg-slate-50"
                      data-testid={standalone ? 'mannu-ai-standalone-close-button' : 'mannu-ai-close-button'}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className={`border-b px-5 py-3 ${standalone ? 'border-black/5 bg-white/80 backdrop-blur-xl' : 'border-slate-100 bg-slate-50/80'}`}>
                  {(!guidedCatalog || activeClient || !pendingPrompt) && (
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleModeSelection('livre')}
                      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${!activeClient ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-600 hover:border-emerald-200 hover:text-emerald-700'}`}
                      data-testid="mannu-ai-mode-free-button"
                    >
                      Conversa Livre
                    </button>
                    <button
                      type="button"
                      onClick={() => handleModeSelection('empresa')}
                      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${activeClient ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-600 hover:border-emerald-200 hover:text-emerald-700'}`}
                      data-testid="mannu-ai-mode-company-button"
                    >
                      Trabalhar com uma Empresa
                    </button>
                    {activeClient && (
                      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-emerald-200 bg-white px-3 py-2" data-testid="mannu-ai-active-client-banner">
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Empresa ativa</p>
                          <p className="text-xs font-medium text-slate-800">{activeClient.razao_social}</p>
                          <p className="text-[11px] text-slate-500">{activeClient.cnpj}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleModeSelection('empresa')}
                          className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-medium text-emerald-700 hover:bg-emerald-100"
                          data-testid="mannu-ai-switch-company-button"
                        >
                          Trocar empresa
                        </button>
                      </div>
                    )}
                  </div>
                  )}
                  {activeClient && !guidedCatalog && (
                    <div className="mt-3 flex flex-wrap gap-2" data-testid="mannu-ai-active-client-actions">
                      <button type="button" onClick={() => handleSend('Verifica pendências')} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-emerald-200 hover:text-emerald-700" data-testid="mannu-ai-quick-action-pendencias">Verificar pendências</button>
                      <button type="button" onClick={() => handleSend('Consulta fiscal completa')} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-emerald-200 hover:text-emerald-700" data-testid="mannu-ai-quick-action-consulta-fiscal">Consulta fiscal</button>
                      <button type="button" onClick={() => handleSend('Gerar DAS')} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-emerald-200 hover:text-emerald-700" data-testid="mannu-ai-quick-action-gerar-das">Gerar DAS</button>
                      <button type="button" onClick={() => handleSend('Me traz o recibo da DCTF deste mês')} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-emerald-200 hover:text-emerald-700" data-testid="mannu-ai-quick-action-recibo-dctf">Recibo DCTF</button>
                      <button type="button" onClick={() => handleSend('Validar documentos desta empresa')} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-emerald-200 hover:text-emerald-700" data-testid="mannu-ai-quick-action-validar-documentos">Validar documentos</button>
                    </div>
                  )}
                  {activeClient && guidedCatalog && (
                    <div className="mt-4 space-y-4" data-testid="mannu-ai-guided-catalog">
                      <section className="rounded-3xl border border-emerald-500/15 bg-emerald-50/40 p-4 shadow-sm" data-testid="mannu-ai-guided-filters-panel">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-slate-900">Refinar catálogo por módulo, competência e status</p>
                            <p className="mt-1 text-xs text-slate-500">Os atalhos abaixo já enviam o contexto da empresa ativa e aplicam os filtros escolhidos no prompt.</p>
                          </div>
                          <span className="rounded-full bg-white px-3 py-1 text-[11px] font-semibold text-emerald-700 shadow-sm">
                            {visibleGuidedCategories.reduce((acc, category) => acc + category.actions.length, 0)} ação(ões) visíveis
                          </span>
                        </div>

                        <div className="mt-4 grid gap-3 lg:grid-cols-[1.1fr,1fr,1fr,1.2fr]">
                          <label className="space-y-1.5" data-testid="mannu-ai-guided-module-filter">
                            <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Módulo</span>
                            <select
                              value={guidedModuleFilter}
                              onChange={(event) => setGuidedModuleFilter(event.target.value)}
                              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none transition focus:border-emerald-500"
                            >
                              {CHAT_OPERACIONAL_MODULE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          </label>

                          <label className="space-y-1.5" data-testid="mannu-ai-guided-competencia-filter">
                            <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Competência</span>
                            <select
                              value={guidedCompetenciaFilter}
                              onChange={(event) => setGuidedCompetenciaFilter(event.target.value)}
                              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none transition focus:border-emerald-500"
                            >
                              {CHAT_OPERACIONAL_COMPETENCIA_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          </label>

                          <label className="space-y-1.5" data-testid="mannu-ai-guided-status-filter">
                            <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Status</span>
                            <select
                              value={guidedStatusFilter}
                              onChange={(event) => setGuidedStatusFilter(event.target.value)}
                              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none transition focus:border-emerald-500"
                            >
                              {CHAT_OPERACIONAL_STATUS_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          </label>

                          <label className="space-y-1.5" data-testid="mannu-ai-guided-search-filter">
                            <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Buscar ação</span>
                            <input
                              value={guidedActionSearch}
                              onChange={(event) => setGuidedActionSearch(event.target.value)}
                              placeholder="Ex.: parcelamento, recibo, tarefa"
                              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none transition focus:border-emerald-500"
                            />
                          </label>
                        </div>

                        {guidedCompetenciaFilter === 'manual' && (
                          <div className="mt-3 grid gap-3 md:max-w-xs">
                            <label className="space-y-1.5" data-testid="mannu-ai-guided-manual-competencia-input">
                              <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Escolher mês</span>
                              <input
                                type="month"
                                value={guidedManualCompetencia}
                                onChange={(event) => setGuidedManualCompetencia(event.target.value)}
                                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none transition focus:border-emerald-500"
                              />
                            </label>
                          </div>
                        )}

                        <div className="mt-3 flex flex-wrap gap-2" data-testid="mannu-ai-guided-filter-chips">
                          {guidedModuleFilter !== 'all' && (
                            <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-600 shadow-sm">Módulo: {CHAT_OPERACIONAL_MODULE_OPTIONS.find((option) => option.value === guidedModuleFilter)?.label}</span>
                          )}
                          {currentCompetenciaLabel && (
                            <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-600 shadow-sm">Competência: {currentCompetenciaLabel}</span>
                          )}
                          {guidedStatusFilter !== 'all' && (
                            <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-600 shadow-sm">Status: {CHAT_OPERACIONAL_STATUS_OPTIONS.find((option) => option.value === guidedStatusFilter)?.label}</span>
                          )}
                        </div>
                      </section>

                      {visibleGuidedCategories.map((category) => (
                        <section key={category.id} className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`mannu-ai-guided-category-${category.id}`}>
                          <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                            <div>
                              <p className="text-sm font-semibold text-slate-900">{category.title}</p>
                              <p className="mt-1 text-xs text-slate-500">{category.description}</p>
                            </div>
                            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">{category.actions.length} ação(ões)</span>
                          </div>
                          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                            {category.actions.map((action) => (
                              <button
                                key={action.id}
                                type="button"
                                onClick={() => handleSend(buildGuidedPrompt(action))}
                                className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-left transition hover:-translate-y-[1px] hover:border-emerald-300 hover:bg-emerald-50"
                                data-testid={`mannu-ai-guided-action-${action.id}`}
                              >
                                <div className="flex flex-wrap items-start justify-between gap-2">
                                  <p className="text-sm font-semibold text-slate-900">{action.label}</p>
                                  <div className="flex flex-wrap gap-1">
                                    {action.supportsCompetencia && <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">competência</span>}
                                    {action.supportsStatus && <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">status</span>}
                                  </div>
                                </div>
                                <p className="mt-1 text-xs leading-5 text-slate-500">{action.description}</p>
                              </button>
                            ))}
                          </div>
                        </section>
                      ))}
                      {visibleGuidedCategories.length === 0 && (
                        <div className="rounded-3xl border border-dashed border-slate-300 bg-white px-5 py-6 text-center" data-testid="mannu-ai-guided-empty-results">
                          <p className="text-sm font-semibold text-slate-900">Nenhuma ação encontrada com os filtros atuais.</p>
                          <p className="mt-2 text-xs leading-6 text-slate-500">Tente limpar a busca, voltar o módulo para “Todos” ou remover o filtro de competência/status.</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className={`flex-1 overflow-y-auto p-5 ${standalone ? 'bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.08),_transparent_42%)]' : 'bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.08),_transparent_40%)]'}`}>
                  {previewDocument && (
                    <div className="mx-auto mb-4 max-w-2xl rounded-3xl border border-emerald-200 bg-white shadow-sm" data-testid="mannu-ai-pdf-preview-card">
                      <div className="flex items-center justify-between gap-3 border-b border-emerald-100 px-4 py-3">
                        <div>
                          <p className="text-sm font-semibold text-emerald-800">{previewDocument.label || 'PDF consultado'}</p>
                          <p className="text-xs text-slate-500">{previewDocument.file_name}</p>
                        </div>
                        <div className="flex gap-2">
                          <button type="button" onClick={() => handleDownloadPdf(previewDocument)} className="rounded-full border border-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50" data-testid="mannu-ai-pdf-download-button">
                            <Download className="mr-1 inline h-3.5 w-3.5" /> Baixar
                          </button>
                          <button type="button" onClick={() => setPreviewDocument(null)} className="rounded-full border border-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50" data-testid="mannu-ai-pdf-close-button">
                            Fechar
                          </button>
                        </div>
                      </div>
                      <div className="h-[300px] bg-slate-50">
                        <iframe src={previewDocument.url} title={previewDocument.file_name} className="h-full w-full" data-testid="mannu-ai-pdf-preview-frame" />
                      </div>
                    </div>
                  )}

                  {dasForm && (
                    <div className="mx-auto mb-4 max-w-2xl rounded-3xl border border-violet-200 bg-violet-50 p-4" data-testid="mannu-ai-das-form">
                      <p className="text-sm font-semibold text-violet-900">Recalcular DAS</p>
                      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                        <input value={dasForm.periodo} onChange={(e) => setDasForm((prev) => ({ ...prev, periodo: e.target.value }))} placeholder="Período AAAAMM" className="rounded-2xl border border-violet-200 bg-white px-4 py-2 text-sm outline-none" data-testid="mannu-ai-das-periodo-input" />
                        <input value={dasForm.valor} onChange={(e) => setDasForm((prev) => ({ ...prev, valor: e.target.value }))} placeholder="Valor da receita bruta" className="rounded-2xl border border-violet-200 bg-white px-4 py-2 text-sm outline-none" data-testid="mannu-ai-das-valor-input" />
                      </div>
                      <div className="mt-3 flex gap-2">
                        <button type="button" onClick={handleRecalculateDas} className="rounded-full bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700" data-testid="mannu-ai-das-submit-button">Gerar PDF</button>
                        <button type="button" onClick={() => setDasForm(null)} className="rounded-full border border-violet-200 bg-white px-4 py-2 text-sm text-violet-700 hover:bg-violet-100" data-testid="mannu-ai-das-cancel-button">Cancelar</button>
                      </div>
                    </div>
                  )}

                  {messages.length === 0 ? (
                    <div className="mx-auto max-w-2xl">
                      {guidedCatalog && pendingPrompt === '__company_context__' && !activeClient && null}
                      {guidedCatalog && !pendingPrompt && !activeClient && (
                        <div className="mb-4 rounded-3xl border border-emerald-500/15 bg-white/90 p-5 shadow-sm" data-testid="mannu-ai-guided-empty-state">
                          <p className="text-sm font-semibold text-slate-900">Conversa livre ativada</p>
                          <p className="mt-2 text-sm text-slate-600">Faça perguntas gerais ao chat ou volte para escolher uma empresa quando quiser ver as ações do Hub.</p>
                        </div>
                      )}
                      {!guidedCatalog && (
                      <div className="rounded-3xl border border-emerald-100 bg-emerald-50/70 p-5">
                        <p className="text-sm font-medium text-emerald-800">Escolha como quer usar a Mannu AI.</p>
                        <p className="mt-2 text-sm text-emerald-700">
                          {commandMode
                            ? 'A Mannu AI vai interpretar a mensagem como comando estruturado e responder em JSON pronto para automação.'
                            : userRole === 'colaborador'
                            ? 'A Mannu AI vai te ajudar com tarefas, rotina operacional e informações básicas do seu escopo.'
                            : 'A Mannu AI responde dúvidas contábeis, status de clientes, tarefas e análises operacionais usando dados reais do sistema.'}
                        </p>
                      </div>
                      )}

                      {!guidedCatalog && (
                      <div className="mt-5 grid gap-3 md:grid-cols-3">
                        <button
                          type="button"
                          onClick={() => handleModeSelection('livre')}
                          className="rounded-3xl border border-slate-200 bg-white p-5 text-left transition-colors hover:border-emerald-300 hover:bg-emerald-50"
                          data-testid="mannu-ai-empty-free-mode-button"
                        >
                          <p className="text-sm font-semibold text-slate-900">Conversa Livre</p>
                          <p className="mt-2 text-sm text-slate-600">Faça perguntas gerais sobre tarefas, clientes, documentos e rotinas do Hub.</p>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleModeSelection('empresa')}
                          className="rounded-3xl border border-slate-200 bg-white p-5 text-left transition-colors hover:border-emerald-300 hover:bg-emerald-50"
                          data-testid="mannu-ai-empty-company-mode-button"
                        >
                          <p className="text-sm font-semibold text-slate-900">Trabalhar com uma Empresa</p>
                          <p className="mt-2 text-sm text-slate-600">Abra o seletor de clientes, escolha a empresa e mantenha toda a conversa dentro desse contexto.</p>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleModeSelection('comando')}
                          className="rounded-3xl border border-slate-200 bg-white p-5 text-left transition-colors hover:border-emerald-300 hover:bg-emerald-50"
                          data-testid="mannu-ai-empty-command-mode-button"
                        >
                          <p className="text-sm font-semibold text-slate-900">Interpretar comando</p>
                          <p className="mt-2 text-sm text-slate-600">Transforme pedidos do WhatsApp em JSON estruturado para automação contábil.</p>
                        </button>
                      </div>
                      )}

                      {!guidedCatalog && (
                      <div className="mt-5 flex flex-wrap gap-2">
                        {suggestions.map((suggestion) => (
                          <button
                            key={suggestion}
                            type="button"
                            onClick={() => handleSend(suggestion)}
                            className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 hover:border-emerald-300 hover:text-emerald-700"
                            data-testid={`mannu-ai-suggestion-${normalizeSuggestionId(suggestion)}`}
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                      )}
                    </div>
                  ) : (
                    <div className="mx-auto max-w-2xl space-y-4">
                      {messages.map((message) => (
                        <div
                          key={message.id}
                          className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                          data-testid={`mannu-ai-message-${message.role}-${message.id}`}
                        >
                          <div className={`max-w-[88%] rounded-3xl px-4 py-3 text-sm leading-relaxed shadow-sm ${message.role === 'user' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-700 border border-slate-200'}`}>
                            <div data-testid={`mannu-ai-message-content-${message.id}`}>
                              {renderMessageContent(message.content)}
                            </div>
                            {message.liveData && (
                              <div className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-3" data-testid={`mannu-ai-live-data-${message.id}`}>
                                <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Consulta em tempo real</p>
                                {message.liveData.batch && (
                                  <div className="mt-3 rounded-2xl border border-emerald-200 bg-white p-3" data-testid={`mannu-ai-batch-summary-${message.id}`}>
                                    <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Execução em lote</p>
                                    <p className="mt-1 text-sm text-slate-700">Período: {message.liveData.batch.periodo}</p>
                                    <p className="text-sm text-slate-700">Clientes verificados: {message.liveData.batch.total_clientes}</p>
                                    <p className="text-sm text-slate-700">Com recibo: {message.liveData.batch.com_recibo}</p>
                                  </div>
                                )}
                                {message.liveData.document_audit && (
                                  <div className="mt-3 rounded-2xl border border-sky-200 bg-white p-3" data-testid={`mannu-ai-document-audit-${message.id}`}>
                                    <p className="text-xs font-semibold uppercase tracking-wide text-sky-700">Validação de documentos</p>
                                    <p className="mt-1 text-sm text-slate-700">Documentos analisados: {message.liveData.document_audit.total_analisados}</p>
                                    <p className="text-sm text-slate-700">Itens para revisar: {message.liveData.document_audit.suspeitos}</p>
                                  </div>
                                )}
                                {message.liveData.document_search && (
                                  <div className="mt-3 rounded-2xl border border-emerald-200 bg-white p-3" data-testid={`mannu-ai-document-search-summary-${message.id}`}>
                                    <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Busca semântica de documentos</p>
                                    <p className="mt-1 text-sm text-slate-700">Resultados encontrados: {message.liveData.document_search.total_encontrados || 0}</p>
                                    {(message.liveData.document_search.semantic_labels || []).length > 0 && (
                                      <p className="mt-1 text-xs text-slate-500">Tipos entendidos: {(message.liveData.document_search.semantic_labels || []).join(' • ')}</p>
                                    )}
                                    {(message.liveData.document_search.period_filters || []).length > 0 && (
                                      <p className="mt-1 text-xs text-slate-500">Períodos considerados: {(message.liveData.document_search.period_filters || []).join(', ')}</p>
                                    )}
                                  </div>
                                )}
                                {(message.liveData.document_panels || []).length > 0 && (
                                  <div className="mt-3 space-y-3" data-testid={`mannu-ai-document-panels-${message.id}`}>
                                    {(message.liveData.document_panels || []).map((panel, panelIndex) => (
                                      <div key={`${panel.id || panel.title || 'panel'}-${panelIndex}`} className="rounded-3xl border border-emerald-200 bg-white p-4 shadow-sm" data-testid={`mannu-ai-document-panel-${normalizeSuggestionId(panel.id || panel.title || panelIndex)}`}>
                                        <div className="flex flex-wrap items-start justify-between gap-3">
                                          <div>
                                            <p className="text-sm font-semibold text-emerald-900">{panel.title}</p>
                                            <p className="mt-1 text-xs text-slate-500">{panel.company_name ? `${panel.company_name} • ` : ''}{panel.subtitle}</p>
                                          </div>
                                          <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-700" data-testid={`mannu-ai-document-panel-count-${normalizeSuggestionId(panel.id)}`}>
                                            {panel.documents.length} documento(s)
                                          </span>
                                        </div>
                                        {(panel.document_types || []).length > 0 && (
                                          <div className="mt-3 flex flex-wrap gap-2">
                                            {(panel.document_types || []).map((typeLabel, typeIndex) => (
                                              <span key={`${panel.id || panel.title || 'panel'}-${typeLabel}-${typeIndex}`} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-medium text-slate-600" data-testid={`mannu-ai-document-panel-type-${normalizeSuggestionId(panel.id || panel.title || panelIndex)}-${normalizeSuggestionId(typeLabel)}-${typeIndex}`}>
                                                {typeLabel}
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                        <div className="mt-3 space-y-2">
                                          {(panel.documents || []).map((document, documentIndex) => renderDocumentActions(document, normalizeSuggestionId(panel.id), documentIndex))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                <div className="mt-2 flex flex-wrap gap-2">
                                  {!(message.liveData.document_panels || []).length && (message.liveData.documents || []).map((document, documentIndex) => renderDocumentActions(document, message.id, documentIndex))}
                                  {(message.liveData.available_actions || []).map((action) => (
                                    action.type === 'recalcular_das' ? (
                                      <button
                                        key={`${action.type}-${action.cnpj}`}
                                        type="button"
                                        onClick={() => setDasForm({ cnpj: action.cnpj, periodo: action.periodo_sugerido, valor: '', client_id: action.client_id })}
                                        className="rounded-full border border-violet-200 bg-white px-3 py-1.5 text-xs font-medium text-violet-700 hover:bg-violet-100"
                                        data-testid={`mannu-ai-action-${action.type}-${message.id}`}
                                      >
                                        <WandSparkles className="mr-1 inline h-3.5 w-3.5" /> {action.label}
                                      </button>
                                    ) : action.type === 'pick_multiple_das_periods' ? (
                                      <button
                                        key={`${action.type}-${action.cnpj}`}
                                        type="button"
                                        onClick={() => setDasOverdueForm(buildDasOverdueFormState(action))}
                                        className="rounded-full border border-emerald-200 bg-white px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
                                        data-testid={`mannu-ai-action-${action.type}-${message.id}`}
                                      >
                                        <FileText className="mr-1 inline h-3.5 w-3.5" /> {action.label}
                                      </button>
                                    ) : action.type === 'generate_mei_installment_current' || action.type === 'generate_mei_installment_overdue' ? (
                                      <button
                                        key={`${action.type}-${action.numero_parcela}`}
                                        type="button"
                                        onClick={() => handleGenerateMeiInstallmentDAS({
                                          client_id: action.client_id,
                                          parcelamento_tipo: action.parcelamento_tipo,
                                          numero_pedido: action.numero_pedido,
                                          numero_parcela: action.numero_parcela,
                                        })}
                                        className="rounded-full border border-emerald-200 bg-white px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
                                        data-testid={`mannu-ai-action-${action.type}-${message.id}`}
                                      >
                                        <Download className="mr-1 inline h-3.5 w-3.5" /> {action.label}
                                      </button>
                                    ) : action.type === 'pick_mei_installment' ? (
                                      <button
                                        key={`${action.type}-${action.numero_pedido}`}
                                        type="button"
                                        onClick={() => setMeiInstallmentForm(buildMeiInstallmentFormState(action))}
                                        className="rounded-full border border-emerald-200 bg-white px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
                                        data-testid={`mannu-ai-action-${action.type}-${message.id}`}
                                      >
                                        <FileText className="mr-1 inline h-3.5 w-3.5" /> {action.label}
                                      </button>
                                    ) : action.type === 'pick_obligation_period' || action.type === 'pick_das_period' ? (
                                      <button
                                        key={`${action.type}-${action.cnpj}-${action.obligation_code || 'das'}`}
                                        type="button"
                                        onClick={() => setObligationLookupForm(buildLookupFormState(action))}
                                        className="rounded-full border border-sky-200 bg-white px-3 py-1.5 text-xs font-medium text-sky-700 hover:bg-sky-100"
                                        data-testid={`mannu-ai-action-${action.type}-${action.obligation_code || 'das'}-${message.id}`}
                                      >
                                        <FileText className="mr-1 inline h-3.5 w-3.5" /> {action.label}
                                      </button>
                                    ) : null
                                  ))}
                                </div>
                                {message.liveData.parcelamento_mei && (
                                  <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid={`mannu-ai-mei-installment-summary-${message.id}`}>
                                    <div className="rounded-xl border border-emerald-200 bg-white p-3">
                                      <p className="text-[11px] uppercase tracking-wide text-slate-500">Total de parcelas</p>
                                      <p className="mt-1 text-sm font-semibold text-slate-900">{message.liveData.parcelamento_mei.quantidade_parcelas || 0}</p>
                                    </div>
                                    <div className="rounded-xl border border-emerald-200 bg-white p-3">
                                      <p className="text-[11px] uppercase tracking-wide text-slate-500">Pagas</p>
                                      <p className="mt-1 text-sm font-semibold text-slate-900">{message.liveData.parcelamento_mei.parcelas_pagas || 0}</p>
                                    </div>
                                    <div className="rounded-xl border border-emerald-200 bg-white p-3">
                                      <p className="text-[11px] uppercase tracking-wide text-slate-500">Em aberto</p>
                                      <p className="mt-1 text-sm font-semibold text-slate-900">{message.liveData.parcelamento_mei.parcelas_em_aberto || 0}</p>
                                    </div>
                                    <div className="rounded-xl border border-emerald-200 bg-white p-3">
                                      <p className="text-[11px] uppercase tracking-wide text-slate-500">Em atraso</p>
                                      <p className="mt-1 text-sm font-semibold text-slate-900">{message.liveData.parcelamento_mei.parcelas_em_atraso || 0}</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                            {message.correctionProposal && userRole === 'admin' && (
                              <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 p-3" data-testid={`mannu-ai-correction-${message.correctionProposal.id}`}>
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                  <div>
                                    <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Correção sugerida</p>
                                    <p className="mt-1 text-sm text-amber-900">Cliente: {message.correctionProposal.client_name}</p>
                                    <p className="mt-1 text-[11px] font-medium text-amber-700" data-testid={`mannu-ai-correction-summary-${message.correctionProposal.id}`}>
                                      Aprove cada item separadamente. Pendentes: {message.correctionProposal.pending_items || 0}
                                    </p>
                                  </div>
                                  <span className="rounded-full border border-amber-300 bg-white px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-amber-800" data-testid={`mannu-ai-correction-status-${message.correctionProposal.id}`}>
                                    {getProposalStatusLabel(message.correctionProposal.status)}
                                  </span>
                                </div>
                                <div className="mt-3 space-y-3" data-testid={`mannu-ai-correction-details-${message.correctionProposal.id}`}>
                                  {(message.correctionProposal.suggestions || []).map((suggestion, suggestionIndex) => (
                                    <div key={suggestion.id || `${message.correctionProposal.id}-${suggestionIndex}`} className="rounded-2xl border border-amber-200 bg-white/80 p-3" data-testid={`mannu-ai-correction-suggestion-${message.correctionProposal.id}-${suggestion.id || suggestionIndex}`}>
                                      <div className="flex flex-wrap items-start justify-between gap-3">
                                        <p className="text-sm font-semibold text-amber-900">{suggestion.title || 'Alteração proposta'}</p>
                                        <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-amber-800" data-testid={`mannu-ai-correction-suggestion-status-${message.correctionProposal.id}-${suggestion.id || suggestionIndex}`}>
                                          {getProposalStatusLabel(suggestion.status || 'pending')}
                                        </span>
                                      </div>
                                      {suggestion.description && (
                                        <p className="mt-1 text-xs text-amber-800">{suggestion.description}</p>
                                      )}

                                      {(suggestion.diffs || []).length > 0 ? (
                                        <div className="mt-3 space-y-2">
                                          <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-700" data-testid={`mannu-ai-correction-diff-title-${message.correctionProposal.id}-${suggestionIndex}`}>
                                            {message.correctionProposal.status === 'approved' ? 'O que foi alterado' : 'O que será alterado'}
                                          </p>
                                          {suggestion.diffs.map((diff, diffIndex) => (
                                            <div key={`${diff.field || diff.label || diffIndex}-${diffIndex}`} className="rounded-2xl border border-slate-200 bg-white p-3" data-testid={`mannu-ai-correction-diff-${message.correctionProposal.id}-${suggestionIndex}-${diffIndex}`}>
                                              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{diff.label || diff.field || 'Campo'}</p>
                                              <div className="mt-2 grid gap-2 md:grid-cols-2">
                                                <div className="rounded-2xl border border-rose-100 bg-rose-50 p-3" data-testid={`mannu-ai-correction-old-value-${message.correctionProposal.id}-${suggestionIndex}-${diffIndex}`}>
                                                  <p className="text-[11px] font-semibold uppercase tracking-wide text-rose-700">Como está hoje</p>
                                                  <p className="mt-1 text-xs text-rose-900 whitespace-pre-wrap">{formatProposalValue(diff.old_value)}</p>
                                                </div>
                                                <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-3" data-testid={`mannu-ai-correction-new-value-${message.correctionProposal.id}-${suggestionIndex}-${diffIndex}`}>
                                                  <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Como ficará</p>
                                                  <p className="mt-1 text-xs text-emerald-900 whitespace-pre-wrap">{formatProposalValue(diff.new_value)}</p>
                                                </div>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      ) : (suggestion.changed_fields || []).length > 0 ? (
                                        <div className="mt-3 flex flex-wrap gap-2" data-testid={`mannu-ai-correction-fields-${message.correctionProposal.id}-${suggestionIndex}`}>
                                          {suggestion.changed_fields.map((field) => (
                                            <span key={field} className="rounded-full border border-amber-200 bg-amber-100 px-2.5 py-1 text-[11px] font-medium text-amber-800">
                                              {field}
                                            </span>
                                          ))}
                                        </div>
                                      ) : null}

                                      {(suggestion.duplicates || []).length > 0 && (
                                        <div className="mt-3" data-testid={`mannu-ai-correction-duplicates-${message.correctionProposal.id}-${suggestion.id || suggestionIndex}`}>
                                          <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">Registros duplicados encontrados</p>
                                          <div className="mt-2 space-y-2">
                                            {suggestion.duplicates.map((duplicate, duplicateIndex) => (
                                              <div key={`${duplicate.nome || duplicate.cpf_cnpj || duplicateIndex}-${duplicateIndex}`} className="rounded-2xl border border-amber-200 bg-amber-100/70 px-3 py-2 text-xs text-amber-900">
                                                {duplicate.nome || 'Sócio sem nome'} {duplicate.cpf_cnpj ? `• ${duplicate.cpf_cnpj}` : ''}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}

                                      {(suggestion.status || 'pending') === 'pending' && (
                                        <div className="mt-3 flex flex-wrap gap-2" data-testid={`mannu-ai-correction-actions-${message.correctionProposal.id}-${suggestion.id || suggestionIndex}`}>
                                          <button
                                            type="button"
                                            onClick={() => handleCorrectionAction(message.correctionProposal.id, 'approve', suggestion.id)}
                                            className="rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700"
                                            data-testid={`mannu-ai-approve-correction-${message.correctionProposal.id}-${suggestion.id || suggestionIndex}`}
                                          >
                                            Aprovar item
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => handleCorrectionAction(message.correctionProposal.id, 'reject', suggestion.id)}
                                            className="rounded-full border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100"
                                            data-testid={`mannu-ai-reject-correction-${message.correctionProposal.id}-${suggestion.id || suggestionIndex}`}
                                          >
                                            Rejeitar item
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div ref={endRef} />
                </div>

                <div className="border-t border-slate-200 p-4">
                  {pendingPrompt && (
                    <div className="mx-auto mb-4 max-w-2xl rounded-3xl border border-amber-200 bg-amber-50 p-4 shadow-sm" data-testid="mannu-ai-client-selector">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-amber-800">{pendingPrompt === '__company_context__' ? 'Selecione uma empresa' : 'Selecione o cliente para continuar'}</p>
                          <p className="mt-1 text-xs text-amber-700">
                            {pendingPrompt === '__company_context__'
                              ? 'Escolha uma empresa cadastrada para ativar o contexto desta conversa.'
                              : 'A Mannu AI precisa do cliente correto para consultar dados reais e o SERPRO.'}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => searchClients('').catch(() => toast.error('Erro ao carregar clientes cadastrados'))}
                          className="rounded-full border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100"
                          data-testid="mannu-ai-load-all-clients-button"
                        >
                          Ver clientes cadastrados
                        </button>
                      </div>
                      <input
                        value={clientSearch}
                        onChange={(event) => {
                          const nextValue = event.target.value;
                          setClientSearch(nextValue);
                          if (nextValue.trim().length >= 2) {
                            searchClients(nextValue).catch(() => toast.error('Erro ao buscar clientes'));
                          } else if (nextValue.trim().length === 0) {
                            searchClients('').catch(() => toast.error('Erro ao carregar clientes cadastrados'));
                          }
                        }}
                        placeholder="Buscar cliente por nome ou CNPJ"
                        className="mt-3 w-full rounded-2xl border border-amber-200 bg-white px-4 py-2 text-sm outline-none"
                        data-testid="mannu-ai-client-search-input"
                      />
                      <div className="mt-3 flex items-center justify-between gap-3">
                        <p className="text-xs font-medium uppercase tracking-wide text-amber-800" data-testid="mannu-ai-client-selector-title">
                          Clientes cadastrados no seu escopo
                        </p>
                        <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-amber-800" data-testid="mannu-ai-client-options-count">
                          {clientOptions.length} opção(ões)
                        </span>
                      </div>
                      <div className="mt-3 max-h-56 space-y-2 overflow-y-auto pr-1" data-testid="mannu-ai-client-options-list">
                        {clientOptions.map((client) => (
                          <button
                            key={client.id}
                            type="button"
                            onClick={() => handleClientSelect(client.id)}
                            className="w-full rounded-2xl border border-amber-200 bg-white px-4 py-3 text-left transition-colors hover:border-emerald-300 hover:bg-emerald-50"
                            data-testid={`mannu-ai-client-option-${client.id}`}
                          >
                            <div className="flex flex-wrap items-start justify-between gap-2">
                              <div>
                                <p className="text-sm font-medium text-slate-800">{client.razao_social}</p>
                                <p className="text-xs text-slate-500">{client.cnpj}</p>
                              </div>
                              <span className="rounded-full bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-600" data-testid={`mannu-ai-client-option-status-${client.id}`}>
                                {client.status_rfb || 'sem status'}
                              </span>
                            </div>
                          </button>
                        ))}
                        {clientOptions.length === 0 && (
                          <div className="rounded-2xl border border-dashed border-amber-300 bg-white px-4 py-4 text-sm text-amber-800" data-testid="mannu-ai-client-options-empty-state">
                            Nenhum cliente apareceu ainda. Clique em <strong>Ver clientes cadastrados</strong> ou busque pelo nome/CNPJ para listar as empresas do seu cadastro.
                          </div>
                        )}
                      </div>
                      {guidedCatalog && pendingPrompt === '__company_context__' && !activeClient && (
                        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/70 bg-white/80 px-4 py-3" data-testid="mannu-ai-guided-entry-actions">
                          <div>
                            <p className="text-sm font-semibold text-slate-900">Ou converse direto com o chat</p>
                            <p className="mt-1 text-xs text-slate-600">Esse caminho é para dúvidas gerais, sem abrir as opções por empresa.</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleModeSelection('livre')}
                            className="rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                            data-testid="mannu-ai-guided-free-chat-button"
                          >
                            Conversar com o chat
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {obligationLookupForm && (
                    <div className="mx-auto mb-4 max-w-2xl rounded-3xl border border-sky-200 bg-sky-50 p-4 shadow-sm" data-testid={`mannu-ai-lookup-form-${obligationLookupForm.obligation_code}`}>
                      <p className="text-sm font-semibold text-sky-900">{obligationLookupForm.title}</p>
                      <p className="mt-1 text-xs text-sky-700">{obligationLookupForm.description}</p>
                      {obligationLookupForm.picker_mode === 'year' ? (
                        <input
                          type="number"
                          min="2020"
                          max="2100"
                          value={obligationLookupForm.period}
                          onChange={(event) => setObligationLookupForm((prev) => ({ ...prev, period: event.target.value }))}
                          className="mt-3 w-full rounded-2xl border border-sky-200 bg-white px-4 py-2 text-sm outline-none"
                          data-testid={`mannu-ai-lookup-period-input-${obligationLookupForm.obligation_code}`}
                        />
                      ) : (
                        <input
                          type="month"
                          value={obligationLookupForm.period}
                          onChange={(event) => setObligationLookupForm((prev) => ({ ...prev, period: event.target.value }))}
                          className="mt-3 w-full rounded-2xl border border-sky-200 bg-white px-4 py-2 text-sm outline-none"
                          data-testid={`mannu-ai-lookup-period-input-${obligationLookupForm.obligation_code}`}
                        />
                      )}
                      <div className="mt-3 rounded-2xl border border-sky-200 bg-white px-3 py-2 text-xs font-medium text-sky-800" data-testid={`mannu-ai-lookup-document-hint-${obligationLookupForm.obligation_code}`}>
                        Agora escolha exatamente qual documento você quer buscar.
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {obligationLookupForm.request_options.map((option) => (
                          <button
                            key={`${obligationLookupForm.obligation_code}-${option.value}`}
                            type="button"
                            onClick={() => handleFetchObligationDocuments(option.value)}
                            className={option.value === 'all'
                              ? 'rounded-full bg-sky-600 px-4 py-2 text-xs font-semibold text-white hover:bg-sky-700'
                              : 'rounded-full border border-sky-200 bg-white px-4 py-2 text-xs font-semibold text-sky-700 hover:bg-sky-100'}
                            data-testid={`mannu-ai-lookup-${obligationLookupForm.obligation_code}-${option.value}-button`}
                          >
                            {option.label}
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => setObligationLookupForm(null)}
                          className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                          data-testid={`mannu-ai-lookup-${obligationLookupForm.obligation_code}-cancel-button`}
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}

                  {dasOverdueForm && (
                    <div className="mx-auto mb-4 max-w-2xl rounded-3xl border border-emerald-200 bg-emerald-50 p-4 shadow-sm" data-testid="mannu-ai-das-overdue-form">
                      <p className="text-sm font-semibold text-emerald-900">Modo completo: gerar segunda via em PDF</p>
                      <p className="mt-1 text-xs text-emerald-700">Selecione uma ou várias competências em atraso, informe a data de geração da guia e eu vou buscar as segundas vias em PDF, salvando automaticamente na pasta do cliente.</p>

                      <div className="mt-4 grid gap-2">
                        {dasOverdueForm.period_options.map((option) => {
                          const checked = dasOverdueForm.selected_periods.includes(option.value);
                          return (
                            <label key={option.value} className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-white px-3 py-2 text-sm text-slate-700" data-testid={`mannu-ai-das-overdue-option-${option.value}`}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => handleToggleDasOverduePeriod(option.value)}
                                data-testid={`mannu-ai-das-overdue-checkbox-${option.value}`}
                              />
                              <span>{option.label}</span>
                            </label>
                          );
                        })}
                      </div>

                      <div className="mt-4 grid gap-4 md:grid-cols-[1fr,auto,auto] md:items-end">
                        <label className="space-y-1 text-sm text-emerald-900">
                          <span className="block text-xs font-semibold uppercase tracking-wide text-emerald-700">Data de geração da guia</span>
                          <input
                            type="date"
                            value={dasOverdueForm.generation_date}
                            onChange={(event) => setDasOverdueForm((prev) => ({ ...prev, generation_date: event.target.value }))}
                            className="w-full rounded-2xl border border-emerald-200 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none focus:border-emerald-400"
                            data-testid="mannu-ai-das-overdue-date-input"
                          />
                        </label>

                        <button
                          type="button"
                          onClick={handleGenerateDasOverdueDocuments}
                          disabled={!dasOverdueForm.generation_date || dasOverdueForm.selected_periods.length === 0}
                          className="rounded-full bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                          data-testid="mannu-ai-das-overdue-generate-button"
                        >
                          Gerar PDFs
                        </button>

                        <button
                          type="button"
                          onClick={() => setDasOverdueForm(null)}
                          className="rounded-full border border-emerald-200 bg-white px-4 py-2.5 text-sm font-medium text-emerald-700 hover:bg-emerald-100"
                          data-testid="mannu-ai-das-overdue-cancel-button"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}

                  {meiInstallmentForm && (
                    <div className="mx-auto mb-4 max-w-2xl rounded-3xl border border-emerald-200 bg-emerald-50 p-4 shadow-sm" data-testid="mannu-ai-mei-installment-form">
                      <p className="text-sm font-semibold text-emerald-900">Escolher parcela do parcelamento MEI</p>
                      <p className="mt-1 text-xs text-emerald-700">Escolha a parcela específica para gerar o PDF da guia.</p>

                      <label className="mt-4 block space-y-1 text-sm text-emerald-900">
                        <span className="block text-xs font-semibold uppercase tracking-wide text-emerald-700">Parcela</span>
                        <select
                          value={meiInstallmentForm.numero_parcela}
                          onChange={(event) => setMeiInstallmentForm((prev) => ({ ...prev, numero_parcela: event.target.value }))}
                          className="w-full rounded-2xl border border-emerald-200 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none focus:border-emerald-400"
                          data-testid="mannu-ai-mei-installment-select"
                        >
                          <option value="">Selecione a parcela</option>
                          {meiInstallmentForm.parcel_options.map((option) => (
                            <option key={`${option.numero_parcela}-${option.mes_referencia}`} value={option.numero_parcela}>
                              Parcela {option.numero_parcela} {option.mes_referencia ? `• ${option.mes_referencia}` : ''}
                            </option>
                          ))}
                        </select>
                      </label>

                      <div className="mt-4 flex flex-wrap gap-3">
                        <button
                          type="button"
                          onClick={() => handleGenerateMeiInstallmentDAS({
                            client_id: meiInstallmentForm.client_id,
                            parcelamento_tipo: meiInstallmentForm.parcelamento_tipo,
                            numero_pedido: meiInstallmentForm.numero_pedido,
                            numero_parcela: meiInstallmentForm.numero_parcela,
                          })}
                          disabled={!meiInstallmentForm.numero_parcela}
                          className="rounded-full bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                          data-testid="mannu-ai-mei-installment-generate-button"
                        >
                          Gerar parcela selecionada
                        </button>
                        <button
                          type="button"
                          onClick={() => setMeiInstallmentForm(null)}
                          className="rounded-full border border-emerald-200 bg-white px-4 py-2.5 text-sm font-medium text-emerald-700 hover:bg-emerald-100"
                          data-testid="mannu-ai-mei-installment-cancel-button"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}

                  {!(guidedCatalog && pendingPrompt === '__company_context__' && !activeClient) && (
                  <div className="flex items-end gap-3">
                    <div className="min-w-0 flex-1 rounded-3xl border border-slate-200 bg-slate-50 px-4 py-3 focus-within:border-emerald-300 focus-within:bg-white">
                      {!guidedCatalog && (
                        <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="mannu-ai-mode-toggle-group">
                          <button
                            type="button"
                            onClick={() => setCommandMode(false)}
                            className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${commandMode ? 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-100' : 'bg-emerald-600 text-white'}`}
                            data-testid="mannu-ai-mode-chat-button"
                          >
                            Conversa
                          </button>
                          <button
                            type="button"
                            onClick={() => setCommandMode(true)}
                            className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${commandMode ? 'bg-slate-900 text-white' : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-100'}`}
                            data-testid="mannu-ai-mode-command-button"
                          >
                            Comando JSON
                          </button>
                          <span className="text-xs text-slate-500" data-testid="mannu-ai-mode-helper-text">
                            {commandMode ? 'Saída estruturada para automação empresarial.' : 'Resposta conversacional com contexto do sistema.'}
                          </span>
                        </div>
                      )}
                      <textarea
                        value={input}
                        onChange={(event) => setInput(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' && !event.shiftKey) {
                            event.preventDefault();
                            handleSend();
                          }
                        }}
                        rows={2}
                        placeholder={guidedCatalog
                          ? (activeClient
                            ? 'Ex.: consulte o DAS, peça um documento ou crie uma tarefa para a empresa ativa'
                            : pendingPrompt === '__company_context__'
                              ? 'Selecione uma empresa para liberar as ações guiadas do Hub'
                              : 'Ex.: quais tarefas eu tenho hoje? ou quais clientes estão com pendência fiscal?')
                          : commandMode
                          ? 'Ex.: me manda as guias do INSS em aberto'
                          : userRole === 'colaborador'
                            ? 'Ex.: Quais tarefas eu tenho hoje?'
                            : 'Ex.: Quantos clientes estão com pendência fiscal?'}
                        className="min-h-[56px] w-full resize-none bg-transparent text-sm text-slate-800 outline-none"
                        data-testid="mannu-ai-input"
                      />
                      {(isRecording || isTranscribing) && (
                        <div className="mt-2 flex items-center gap-2 text-xs font-medium text-emerald-700" data-testid="mannu-ai-audio-status">
                          {isTranscribing ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <Mic className="h-3.5 w-3.5" />}
                          {isRecording ? 'Gravando pergunta em áudio...' : 'Transcrevendo áudio com whisper-1...'}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleToggleAudioRecording}
                      disabled={sending || isTranscribing}
                      className={`flex h-12 w-12 items-center justify-center rounded-2xl border ${isRecording ? 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100' : 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'} disabled:cursor-not-allowed disabled:opacity-50`}
                      data-testid="mannu-ai-audio-button"
                    >
                      {isTranscribing ? <LoaderCircle className="h-5 w-5 animate-spin" /> : isRecording ? <Square className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSend()}
                      disabled={sending || isRecording || isTranscribing || !input.trim()}
                      className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                      data-testid="mannu-ai-send-button"
                    >
                      {sending ? <MessageCircleMore className="h-5 w-5 animate-pulse" /> : <SendHorizonal className="h-5 w-5" />}
                    </button>
                  </div>
                  )}
                </div>
              </section>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const normalizeSuggestionId = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, '-');

const renderInlineMarkdown = (line) => {
  const text = String(line || '');
  const parts = [];
  const regex = /\*\*(.*?)\*\*/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(<strong key={`${match[1]}-${match.index}`}>{match[1]}</strong>);
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : text;
};

const renderMessageContent = (content) => {
  const text = String(content || '').replace(/\r/g, '');
  return text.split('\n').map((line, index) => {
    const cleanedLine = line.replace(/^\s*#{1,6}\s*/, '').trimEnd();

    if (!cleanedLine.trim() || /^-{3,}$/.test(cleanedLine.trim())) {
      return <div key={`blank-${index}`} className="h-3" />;
    }

    return (
      <p key={`line-${index}`} className="whitespace-pre-wrap break-words">
        {renderInlineMarkdown(cleanedLine)}
      </p>
    );
  });
};

const getProposalStatusLabel = (status) => {
  const labels = {
    pending: 'Pendente',
    partially_processed: 'Parcial',
    approved: 'Aprovado',
    rejected: 'Rejeitado',
  };

  return labels[status] || status || 'Pendente';
};

const formatProposalValue = (value) => {
  if (value === null || value === undefined || value === '') return 'Não informado';
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  if (Array.isArray(value)) {
    if (value.length === 0) return 'Sem registros';

    return value.map((item) => {
      if (item && typeof item === 'object') {
        if (item.nome || item.cpf_cnpj) {
          return `${item.nome || 'Sem nome'}${item.cpf_cnpj ? ` • ${item.cpf_cnpj}` : ''}`;
        }
        return JSON.stringify(item, null, 2);
      }
      return String(item);
    }).join('\n');
  }
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

export default MannuAIWidget;
